/**
 * MatchedTransactions — kompakt tablo: sol Odeme, sag Fatura.
 * Minimal, sade. Multi-invoice icin satir basina ek fatura listesi.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Unlink, Search, CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (v) => (Number(v) || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_BADGE = {
    sales: { l: 'S', cls: 'bg-emerald-50 text-emerald-700' },
    purchase: { l: 'A', cls: 'bg-blue-50 text-blue-700' },
    sales_credit: { l: 'S-IADE', cls: 'bg-rose-50 text-rose-700' },
    purchase_credit: { l: 'A-IADE', cls: 'bg-amber-50 text-amber-700' },
};

export default function MatchedTransactions({ items = [], onChange, onLocalUpdate }) {
    const [busy, setBusy] = useState(null);
    const [bulk, setBulk] = useState(false);
    const [q, setQ] = useState('');
    const [selected, setSelected] = useState(new Set());
    const [expanded, setExpanded] = useState(new Set()); // dropdown acik olanlar (tx id veya invoice id)
    const [groupBy, setGroupBy] = useState('tx'); // 'tx' | 'invoice'

    // Fatura bazinda grupla: bir invoice'i paylasan tum tx'leri tek satirda topla
    const invoiceGroups = useMemo(() => {
        const map = new Map(); // invoice_id -> { inv_meta, txs: [...] }
        (items || []).forEach(tx => {
            const invs = tx.matched_invoices || [];
            invs.forEach(inv => {
                const key = inv.invoice_id;
                if (!key) return;
                if (!map.has(key)) {
                    map.set(key, {
                        invoice_id: inv.invoice_id,
                        invoice_number: inv.invoice_number,
                        invoice_date: inv.invoice_date,
                        invoice_total: inv.invoice_total,
                        invoice_type: inv.invoice_type,
                        txs: [],
                    });
                }
                map.get(key).txs.push({
                    ...tx,
                    allocated_amount: inv.allocated_amount,
                });
            });
        });
        return Array.from(map.values()).sort((a, b) => (b.invoice_date || '').localeCompare(a.invoice_date || ''));
    }, [items]);

    const filtered = useMemo(() => {
        if (!q.trim()) return items;
        const qq = q.toLowerCase();
        return items.filter(t => {
            const invs = (t.matched_invoices || []).map(i => `${i.invoice_number}`).join(' ');
            const hay = `${t.communication || ''} ${t.reference || ''} ${t.counterparty_name || ''} ${t.amount} ${invs}`.toLowerCase();
            return hay.includes(qq);
        });
    }, [items, q]);

    const filteredInvGroups = useMemo(() => {
        if (!q.trim()) return invoiceGroups;
        const qq = q.toLowerCase();
        return invoiceGroups.filter(g => {
            const txInfo = g.txs.map(t => `${t.communication || ''} ${t.reference || ''} ${t.counterparty_name || ''}`).join(' ');
            const hay = `${g.invoice_number || ''} ${g.invoice_total} ${txInfo}`.toLowerCase();
            return hay.includes(qq);
        });
    }, [invoiceGroups, q]);

    // Otomatik mod onerisi: ayni invoice'a baglanmis birden fazla tx varsa "invoice" modu daha anlamli
    const hasInvoiceWithMultiTx = useMemo(
        () => invoiceGroups.some(g => g.txs.length > 1),
        [invoiceGroups]
    );

    const handleUnlink = async (txId) => {
        if (!window.confirm('Bu eslesme bozulacak. Devam et?')) return;
        setBusy(txId);
        try {
            await axios.post(`${API}/api/accounting/reconciliation/unlink`,
                { transaction_id: txId }, { headers: headers() });
            toast.success('Eslesme bozuldu');
            // Optimistic: bu tx'i matched listeden cikar
            if (onLocalUpdate) {
                onLocalUpdate(prev => ({
                    ...prev,
                    matched_transactions: (prev.matched_transactions || []).filter(t => t.id !== txId),
                    counts: { ...prev.counts, matched_tx: Math.max(0, (prev.counts?.matched_tx || 0) - 1) },
                }));
            } else {
                onChange?.();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setBusy(null); }
    };

    const handleAcceptDiff = async (txId, diff) => {
        if (!window.confirm(`${fmt(diff)} EUR fark BANKA KOMISYONU olarak kabul edilecek.\nFatura(lar) tam kapatilacak ve cari hesap sifirlanir. Devam et?`)) return;
        setBusy(txId);
        try {
            const r = await axios.post(`${API}/api/accounting/reconciliation/accept-difference`,
                { transaction_id: txId }, { headers: headers() });
            toast.success(`${fmt(r.data.bank_fee_amount)} EUR komisyon olarak kaydedildi`);
            // Optimistic: bu tx'i had_difference + bank_fee_amount ile guncelle
            if (onLocalUpdate) {
                onLocalUpdate(prev => ({
                    ...prev,
                    matched_transactions: (prev.matched_transactions || []).map(t =>
                        t.id === txId ? { ...t, had_difference: true, bank_fee_amount: r.data.bank_fee_amount } : t
                    ),
                }));
            } else {
                onChange?.();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Hata');
        } finally { setBusy(null); }
    };

    const handleBulkUnlink = async () => {
        if (selected.size === 0) return;
        if (!window.confirm(`${selected.size} eslesme bozulacak. Devam et?`)) return;
        setBulk(true);
        let ok = 0, fail = 0;
        const okIds = new Set();
        for (const txId of Array.from(selected)) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await axios.post(`${API}/api/accounting/reconciliation/unlink`,
                    { transaction_id: txId }, { headers: headers() });
                ok++;
                okIds.add(txId);
            } catch (e) { fail++; }
        }
        setBulk(false);
        setSelected(new Set());
        if (fail === 0) toast.success(`${ok} eslesme bozuldu`);
        else toast.warning(`${ok} basarili · ${fail} hata`);
        if (onLocalUpdate && okIds.size > 0) {
            onLocalUpdate(prev => ({
                ...prev,
                matched_transactions: (prev.matched_transactions || []).filter(t => !okIds.has(t.id)),
                counts: { ...prev.counts, matched_tx: Math.max(0, (prev.counts?.matched_tx || 0) - okIds.size) },
            }));
        } else {
            onChange?.();
        }
    };

    const toggleSel = (id) => setSelected(s => {
        const next = new Set(s);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    if (items.length === 0) {
        return (
            <div className="bg-emerald-50/50 border border-emerald-200 rounded-2xl p-12 text-center" data-testid="acc-matched-empty">
                <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-3" />
                <p className="text-sm font-semibold text-emerald-900">Henuz eslesmis transfer yok</p>
                <p className="text-xs text-emerald-700 mt-1">Inbox'tan eslestirme yaptiginda burada listelenecek</p>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="acc-matched-transactions">
            {/* Toolbar */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="relative w-72">
                    <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <input value={q} onChange={e => setQ(e.target.value)}
                        placeholder="Ref, fatura no, karsi taraf..."
                        className="w-full h-8 pl-6 pr-2 bg-white border border-[#E5E7EB] rounded text-[12px]"
                        data-testid="acc-matched-search" />
                </div>
                {/* Grupla toggle: Transfer / Fatura */}
                <div className="flex items-center gap-1 bg-[#F3F4F6] rounded-lg p-0.5" data-testid="acc-matched-groupby">
                    <span className="text-[10px] uppercase tracking-wider text-[#9CA3AF] font-semibold px-2">Grupla:</span>
                    <button onClick={() => { setGroupBy('tx'); setExpanded(new Set()); }}
                        className={`px-3 h-7 rounded-md text-[11px] font-semibold transition ${groupBy === 'tx' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280] hover:text-[#111]'}`}
                        data-testid="acc-matched-groupby-tx">
                        Transfer ({items.length})
                    </button>
                    <button onClick={() => { setGroupBy('invoice'); setExpanded(new Set()); }}
                        className={`px-3 h-7 rounded-md text-[11px] font-semibold transition flex items-center gap-1 ${groupBy === 'invoice' ? 'bg-white text-[#111] shadow-sm' : 'text-[#6B7280] hover:text-[#111]'}`}
                        data-testid="acc-matched-groupby-invoice">
                        Fatura ({invoiceGroups.length})
                        {hasInvoiceWithMultiTx && groupBy !== 'invoice' && (
                            <span className="text-[8px] px-1 py-0.5 rounded bg-amber-400 text-[#111] font-bold" title="Bu firmada bir faturaya birden fazla odeme yapilmis — Fatura modu daha okunabilir">!</span>
                        )}
                    </button>
                </div>
                <span className="text-[11px] text-[#6B7280]">{groupBy === 'tx' ? filtered.length : filteredInvGroups.length} {groupBy === 'tx' ? 'transfer' : 'fatura'}</span>
                {/* Toplu secim shortcut'lari (sadece tx modunda) */}
                {groupBy === 'tx' && (
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setSelected(new Set(filtered.map(t => t.id)))}
                            className="h-7 px-2.5 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#374151] text-[10px] font-semibold rounded-md flex items-center gap-1"
                            data-testid="acc-matched-select-all">
                            ✓ Tumunu Sec
                        </button>
                        <button
                            onClick={() => {
                                const ids = filtered
                                    .filter(tx => {
                                        const invList = tx.matched_invoices || [];
                                        const allocNet = invList.reduce((s, i) => {
                                            const amt = Math.abs(i.allocated_amount) || Math.abs(i.invoice_total) || 0;
                                            const t = i.invoice_type || '';
                                            return s + ((t === 'purchase' || t === 'sales_credit') ? -amt : amt);
                                        }, 0);
                                        const d = Math.abs(Math.abs(tx.amount || 0) - Math.abs(allocNet));
                                        return d > 0.5 || !!tx.had_difference;
                                    })
                                    .map(t => t.id);
                                setSelected(new Set(ids));
                            }}
                            className="h-7 px-2.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 text-[10px] font-semibold rounded-md flex items-center gap-1"
                            data-testid="acc-matched-select-mismatched"
                            title="Allokasyonu transfer tutarina esit olmayan eslesmeleri sec">
                            ⚠ Farkli Olanlari Sec
                        </button>
                    </div>
                )}
                {selected.size > 0 && (
                    <button onClick={handleBulkUnlink} disabled={bulk}
                        className="ml-auto h-8 px-3 bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold rounded-lg flex items-center gap-1 disabled:opacity-50"
                        data-testid="acc-matched-bulk-unlink">
                        {bulk ? <Loader2 size={11} className="animate-spin" /> : <Unlink size={11} />}
                        Bozulanlar ({selected.size})
                    </button>
                )}
            </div>

            {/* Kompakt tablo */}
            <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                    {groupBy === 'tx' ? (
                    <table className="w-full text-[12px]">
                        <thead className="bg-[#FAFAFA]">
                            <tr className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
                                <th className="w-8 px-2 py-2"></th>
                                <th colSpan={3} className="px-3 py-2 text-left font-semibold border-r border-[#F3F4F6]">ÖDEME (Banka)</th>
                                <th colSpan={4} className="px-3 py-2 text-left font-semibold">FATURA</th>
                                <th className="w-20 px-2 py-2"></th>
                            </tr>
                            <tr className="text-[10px] text-[#9CA3AF] uppercase border-b border-[#E5E7EB]">
                                <th></th>
                                <th className="px-3 py-1.5 text-left font-medium">Tarih</th>
                                <th className="px-3 py-1.5 text-right font-medium">Tutar</th>
                                <th className="px-3 py-1.5 text-left font-medium border-r border-[#F3F4F6]">Aciklama</th>
                                <th className="px-3 py-1.5 text-left font-medium">No</th>
                                <th className="px-3 py-1.5 text-left font-medium">Tarih</th>
                                <th className="px-3 py-1.5 text-left font-medium">Tip</th>
                                <th className="px-3 py-1.5 text-right font-medium">Alloke</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                            {filtered.map(tx => {
                                const isPos = (tx.amount || 0) >= 0;
                                const isSel = selected.has(tx.id);
                                const invs = tx.matched_invoices || [];
                                const isMulti = invs.length > 1;
                                const isExpanded = expanded.has(tx.id);
                                const first = invs[0] || {};
                                // Allocation NET hesabi: tip bazli signed (mahsup mantigi).
                                // sales/purchase_credit → + ; purchase/sales_credit → -
                                // Boylece: gelen +1.086,10 = 6.215(S) - 5.128,90(A) → diff=0
                                const allocNetSigned = invs.reduce((s, i) => {
                                    const amt = Math.abs(i.allocated_amount) || Math.abs(i.invoice_total) || 0;
                                    const t = i.invoice_type || '';
                                    const sign = (t === 'purchase' || t === 'sales_credit') ? -1 : +1;
                                    return s + sign * amt;
                                }, 0);
                                const txAbs = Math.abs(tx.amount || 0);
                                const diff = Math.abs(txAbs - Math.abs(allocNetSigned));
                                const hadDiff = !!tx.had_difference;
                                const bankFee = tx.bank_fee_amount || 0;
                                // Sadece makul fark (max 50 EUR veya %5) icin "Fark kabul et" gosterilir.
                                const reasonableFeeLimit = Math.max(50, Math.abs(allocNetSigned) * 0.05);
                                const showAcceptBtn = !hadDiff && diff > 0.5 && diff <= reasonableFeeLimit;
                                const tooBigDiff = !hadDiff && diff > reasonableFeeLimit;
                                return (
                                    <React.Fragment key={tx.id}>
                                        <tr className={`hover:bg-[#F9FAFB] ${isSel ? 'bg-blue-50' : ''} ${hadDiff ? 'bg-purple-50/30' : ''}`} data-testid={`acc-matched-tx-${tx.id}`}>
                                            <td className="px-2 align-top pt-2">
                                                <input type="checkbox" checked={isSel} onChange={() => toggleSel(tx.id)} className="w-3.5 h-3.5 accent-blue-600" />
                                            </td>
                                            <td className="px-3 py-2 text-[#6B7280] whitespace-nowrap align-top">{tx.transaction_date}</td>
                                            <td className={`px-3 py-2 text-right font-bold whitespace-nowrap align-top ${isPos ? 'text-emerald-600' : 'text-red-500'}`}>
                                                {isPos ? '+' : ''}{fmt(tx.amount)}
                                            </td>
                                            <td className="px-3 py-2 max-w-[260px] truncate text-[#374151] align-top border-r border-[#F3F4F6]" title={tx.communication || ''}>
                                                {tx.communication || tx.reference || '—'}
                                                {isMulti && (
                                                    <button
                                                        onClick={() => setExpanded(s => { const n = new Set(s); n.has(tx.id) ? n.delete(tx.id) : n.add(tx.id); return n; })}
                                                        className="ml-2 inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded bg-indigo-500 hover:bg-indigo-600 text-white font-bold cursor-pointer transition"
                                                        title={isExpanded ? `${invs.length} faturayi gizle` : `${invs.length} faturanin tumunu goster`}
                                                        data-testid={`acc-matched-toggle-${tx.id}`}>
                                                        {isExpanded ? <ChevronDown size={9} /> : <ChevronRight size={9} />}
                                                        +{invs.length - 1} fatura
                                                    </button>
                                                )}
                                                {hadDiff && bankFee > 0 && (
                                                    <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-purple-500 text-white font-bold" title="Komisyon kabul edildi">
                                                        +{fmt(bankFee)} KOMISYON
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-semibold text-[#111] align-top whitespace-nowrap">
                                                #{first.invoice_number || '?'}
                                            </td>
                                            <td className="px-3 py-2 text-[#6B7280] align-top whitespace-nowrap">{first.invoice_date || '—'}</td>
                                            <td className="px-3 py-2 align-top">
                                                {first.invoice_type && (
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${(TYPE_BADGE[first.invoice_type] || { cls: 'bg-gray-100 text-gray-500' }).cls}`}>
                                                        {(TYPE_BADGE[first.invoice_type] || { l: '?' }).l}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-[#111] font-medium align-top whitespace-nowrap">
                                                {first.allocated_amount > 0 ? fmt(first.allocated_amount) : fmt(first.invoice_total)}
                                            </td>
                                            <td className="px-2 py-1.5 align-top">
                                                <div className="flex flex-col gap-1 items-end">
                                                    {showAcceptBtn && (
                                                        <button onClick={() => handleAcceptDiff(tx.id, diff)} disabled={busy === tx.id}
                                                            className="h-6 px-2 bg-amber-100 hover:bg-amber-500 hover:text-white border border-amber-300 text-amber-700 text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50 whitespace-nowrap"
                                                            title={`${fmt(diff)} EUR fark var — banka komisyonu olarak kabul et`}
                                                            data-testid={`acc-matched-accept-diff-${tx.id}`}>
                                                            💰 Fark Δ{fmt(diff)}
                                                        </button>
                                                    )}
                                                    {tooBigDiff && (
                                                        <span
                                                            className="h-6 px-2 bg-red-50 border border-red-200 text-red-600 text-[10px] font-bold rounded flex items-center gap-1 whitespace-nowrap"
                                                            title={`Fark cok buyuk (${fmt(diff)} EUR) — banka komisyonu olamaz. Boz ve dogru fatura(lar) ile yeniden eslestir.`}
                                                            data-testid={`acc-matched-bigdiff-${tx.id}`}>
                                                            ⚠ Eksik Δ{fmt(diff)}
                                                        </span>
                                                    )}
                                                    <button onClick={() => handleUnlink(tx.id)} disabled={busy === tx.id}
                                                        className="h-6 px-2 bg-red-50 hover:bg-red-500 hover:text-white border border-red-200 text-red-600 text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50"
                                                        data-testid={`acc-matched-unlink-${tx.id}`}>
                                                        {busy === tx.id ? <Loader2 size={9} className="animate-spin" /> : <Unlink size={9} />}
                                                        Boz
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Ek faturalar (multi-invoice) — sadece expanded ise gosterilir */}
                                        {isMulti && isExpanded && invs.slice(1).map(inv => (
                                            <tr key={inv.invoice_id} className="bg-indigo-50/30">
                                                <td colSpan={4} className="px-3 py-1.5 text-right text-[10px] text-[#9CA3AF] border-r border-[#F3F4F6] italic">↑ ayni eslesme</td>
                                                <td className="px-3 py-1.5 font-semibold text-[#111] whitespace-nowrap">#{inv.invoice_number}</td>
                                                <td className="px-3 py-1.5 text-[#6B7280] whitespace-nowrap">{inv.invoice_date || '—'}</td>
                                                <td className="px-3 py-1.5">
                                                    {inv.invoice_type && (
                                                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${(TYPE_BADGE[inv.invoice_type] || { cls: 'bg-gray-100 text-gray-500' }).cls}`}>
                                                            {(TYPE_BADGE[inv.invoice_type] || { l: '?' }).l}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-1.5 text-right text-[#111] font-medium whitespace-nowrap">
                                                    {inv.allocated_amount > 0 ? fmt(inv.allocated_amount) : fmt(inv.invoice_total)}
                                                </td>
                                                <td></td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    ) : (
                    /* INVOICE-BAZINDA: bir faturaya baglanmis tum tx'leri grupla */
                    <table className="w-full text-[12px]" data-testid="acc-matched-invoice-table">
                        <thead className="bg-[#FAFAFA]">
                            <tr className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">
                                <th colSpan={5} className="px-3 py-2 text-left font-semibold border-r border-[#F3F4F6]">FATURA</th>
                                <th colSpan={2} className="px-3 py-2 text-left font-semibold">ÖDEME OZETI</th>
                                <th className="w-20 px-2 py-2"></th>
                            </tr>
                            <tr className="text-[10px] text-[#9CA3AF] uppercase border-b border-[#E5E7EB]">
                                <th className="w-6 px-2 py-1.5"></th>
                                <th className="px-3 py-1.5 text-left font-medium">No</th>
                                <th className="px-3 py-1.5 text-left font-medium">Tarih</th>
                                <th className="px-3 py-1.5 text-left font-medium">Tip</th>
                                <th className="px-3 py-1.5 text-right font-medium border-r border-[#F3F4F6]">Toplam</th>
                                <th className="px-3 py-1.5 text-center font-medium">Odeme Sayisi</th>
                                <th className="px-3 py-1.5 text-right font-medium">Odenen</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#F3F4F6]">
                            {filteredInvGroups.map(g => {
                                const isExpanded = expanded.has(`inv-${g.invoice_id}`);
                                const totalPaid = g.txs.reduce((s, t) => s + (Math.abs(t.allocated_amount || 0)), 0);
                                const txCount = g.txs.length;
                                const isMultiTx = txCount > 1;
                                return (
                                    <React.Fragment key={g.invoice_id}>
                                        <tr className="hover:bg-[#F9FAFB]" data-testid={`acc-matched-inv-${g.invoice_id}`}>
                                            <td className="px-2 py-2 align-top">
                                                {isMultiTx && (
                                                    <button
                                                        onClick={() => setExpanded(s => { const n = new Set(s); const k = `inv-${g.invoice_id}`; n.has(k) ? n.delete(k) : n.add(k); return n; })}
                                                        className="text-indigo-600 hover:text-indigo-800"
                                                        title={isExpanded ? 'Odemeleri gizle' : `${txCount} odemeyi goster`}
                                                        data-testid={`acc-matched-inv-toggle-${g.invoice_id}`}>
                                                        {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 font-semibold text-[#111] align-top whitespace-nowrap">#{g.invoice_number}</td>
                                            <td className="px-3 py-2 text-[#6B7280] align-top whitespace-nowrap">{g.invoice_date || '—'}</td>
                                            <td className="px-3 py-2 align-top">
                                                {g.invoice_type && (
                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${(TYPE_BADGE[g.invoice_type] || { cls: 'bg-gray-100 text-gray-500' }).cls}`}>
                                                        {(TYPE_BADGE[g.invoice_type] || { l: '?' }).l}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-[#111] font-bold align-top whitespace-nowrap border-r border-[#F3F4F6]">{fmt(g.invoice_total)}</td>
                                            <td className="px-3 py-2 text-center align-top">
                                                {isMultiTx ? (
                                                    <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-500 text-white font-bold">{txCount} taksit</span>
                                                ) : (
                                                    <span className="text-[10px] text-[#9CA3AF]">tek odeme</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-emerald-700 font-bold align-top whitespace-nowrap">{fmt(totalPaid)}</td>
                                            <td className="px-2 py-1.5 align-top text-right">
                                                {!isMultiTx && (
                                                    <button onClick={() => handleUnlink(g.txs[0].id)} disabled={busy === g.txs[0].id}
                                                        className="h-6 px-2 bg-red-50 hover:bg-red-500 hover:text-white border border-red-200 text-red-600 text-[10px] font-bold rounded flex items-center gap-1 disabled:opacity-50 ml-auto"
                                                        data-testid={`acc-matched-inv-unlink-${g.invoice_id}`}>
                                                        {busy === g.txs[0].id ? <Loader2 size={9} className="animate-spin" /> : <Unlink size={9} />}
                                                        Boz
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Taksit/odeme detaylari (dropdown) */}
                                        {isMultiTx && isExpanded && g.txs.map(tx => (
                                            <tr key={tx.id} className="bg-indigo-50/30">
                                                <td></td>
                                                <td colSpan={4} className="px-3 py-1.5 border-r border-[#F3F4F6]">
                                                    <div className="flex items-center gap-2 text-[11px]">
                                                        <span className="text-[#6B7280]">{tx.transaction_date}</span>
                                                        <span className={`font-bold ${(tx.amount || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                                            {(tx.amount || 0) >= 0 ? '+' : ''}{fmt(tx.amount)}
                                                        </span>
                                                        <span className="text-[#9CA3AF] truncate max-w-[300px]" title={tx.communication}>· {tx.communication || tx.reference || '—'}</span>
                                                    </div>
                                                </td>
                                                <td></td>
                                                <td className="px-3 py-1.5 text-right text-[#111] font-medium whitespace-nowrap">{fmt(Math.abs(tx.allocated_amount || 0))}</td>
                                                <td className="px-2 py-1.5 text-right">
                                                    <button onClick={() => handleUnlink(tx.id)} disabled={busy === tx.id}
                                                        className="h-6 px-2 bg-red-50 hover:bg-red-500 hover:text-white border border-red-200 text-red-600 text-[10px] font-bold rounded inline-flex items-center gap-1 disabled:opacity-50"
                                                        data-testid={`acc-matched-inv-tx-unlink-${tx.id}`}>
                                                        {busy === tx.id ? <Loader2 size={9} className="animate-spin" /> : <Unlink size={9} />}
                                                        Boz
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    )}
                </div>
            </div>
        </div>
    );
}
